/**
 * 頁 9 — 出庫單詳情（`ui-spec` 頁 9）
 *
 * 一眼睇齊：單頭（類型／去向／取貨時間／收貨方／送貨碼）＋ 各行（申報 vs 實際）＋
 * 執貨摘要（差異）＋ **而家可以做嘅下一步**。
 *
 * 紅線
 *   · 「下一步」一律用後端回嘅 `next`（＝ `nextStatuses()`）—— 前端**唔准**自己列狀態
 *   · `draft` → 發 WhatsApp 通知（`OutboundSendPanel`）／取消
 *   · `sent` → 去執貨頁（`/outbound/:id/picklist`；要 `sent` 先准執貨）
 *   · `acknowledged` → 交收完成（`OutboundDeliverForm`）
 *   · `direct_sale` 交收完仲未完：要場後結算（賣出／剩貨／損壞）才准 `closed`
 *
 * ⚠️ 區塊（各行／摘要／下一步）住喺 `components/OutboundDetailSections.tsx`
 *    （硬規則 B：頁面檔 ≤200 行）—— 本頁只負責載入／單頭／組裝。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import DeliveryEvidencePanel from '../components/DeliveryEvidencePanel';
import { Field, LinesSection, SummarySection } from '../components/OutboundDetailSections';
import { OutboundStatusChip, destinationLabel, kindLabelKey, modeLabelKey } from '../components/OutboundStatusChip';
import { btnGhost, card, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Me, type OutboundDetail as Detail } from '../lib/api';

export default function OutboundDetail({ user }: { user: Me }) {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await api.outboundOrder(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const order = detail?.order ?? null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('out.detailTitle')}</h2>
        <Link to="/outbound" style={btnGhost}>
          {t('out.back')}
        </Link>
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}
        </p>
      )}

      {order && detail && (
        <>
          <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '16px' }}>{order.orderNo}</strong>
              <OutboundStatusChip status={order.status} />
              <span style={hintStyle}>{t(kindLabelKey(order.outboundKind))}</span>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Field
                labelKey="out.destination"
                value={destinationLabel(
                  { labelZhHk: order.destinationLabelZhHk, labelEn: order.destinationLabelEn },
                  i18n.language,
                )}
              />
              <Field labelKey="out.deliveryMode" value={t(modeLabelKey(order.deliveryMode))} />
              <Field labelKey="out.pickupAt" value={order.pickupAt ?? '—'} />
              <Field labelKey="out.consignee" value={order.consigneeName ?? '—'} />
              {order.consigneeAddress && (
                <Field labelKey="out.address" value={order.consigneeAddress} />
              )}
              {order.deliveryCode && <Field labelKey="out.deliveryCode" value={order.deliveryCode} />}
              {order.waSentAt && <Field labelKey="out.waSentAt" value={order.waSentAt.slice(0, 16).replace('T', ' ')} />}
              {order.pickedAt && <Field labelKey="out.pickedAt" value={order.pickedAt} />}
              {order.deliveredAt && <Field labelKey="out.deliveredAt" value={order.deliveredAt} />}
              {detail.toLoc && <Field labelKey="out.toLoc" value={detail.toLoc} />}
              {order.note && <Field labelKey="out.note" value={order.note} />}
            </div>

            {/* P1-9（H1）：送貨單一律由系統出（含送貨碼）—— 開獨立列印頁，用瀏覽器列印／另存 PDF。
                直銷場單冇送貨單（後端 400）；前端連掣都唔顯示。 */}
            {order.outboundKind === 'delivery' && (
              <div style={{ marginTop: '10px' }}>
                <a
                  href={api.deliveryNoteUrl(order.id, i18n.language)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                >
                  {t('out.printDeliveryNote')}
                </a>
                <p style={{ ...hintStyle, marginTop: '6px' }}>{t('out.printDeliveryNoteHint')}</p>
              </div>
            )}
          </section>

          <LinesSection lines={detail.lines} />

          {/* P1-9（H3／H4）：交收證據 —— 缺「客戶印章／簽名」相就紅標，相可以後補 */}
          {order.outboundKind === 'delivery' && (
            <DeliveryEvidencePanel
              orderId={order.id}
              hasChop={order.hasDeliveryNoteChop}
              missing={order.deliveryEvidence.missing}
              user={user}
              onUploaded={load}
            />
          )}

          <SummarySection detail={detail} user={user} onChanged={load} />
        </>
      )}
    </>
  );
}
